import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./ProfileSetup.css";
import { useLanguage } from "../i18n";

function localDateValue(date) {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

const randomUsernames = [
  "King_of_Venus", "ShadowPilot", "DesertShark", "NovaRider",
  "SolarWolf", "IronGalaxy", "NightFalcon", "QuantumRex",
  "StarVoyager", "EchoTitan", "NebulaHunter"
];

export default function ProfileSetup({ onClose }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [identityType, setIdentityType] = useState("realName");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [birth, setBirth] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const generateUsername = () => {
    const name = randomUsernames[Math.floor(Math.random() * randomUsernames.length)];
    setUsername(`${name}${Math.floor(100 + Math.random() * 900)}`);
    setIdentityType("pseudonym");
    setError("");
  };

  const handleContinue = async () => {
    setError("");
    if (identityType === "realName" && (!firstName.trim() || !lastName.trim())) {
      setError(t("identityRequired"));
      return;
    }
    if (identityType === "pseudonym" && !username.trim()) {
      setError(t("identityRequired"));
      return;
    }

    if (!birth) {
      setError(t("birthRequired"));
      return;
    }
    const birthDate = new Date(`${birth}T00:00:00`);
    const today = new Date();
    if (Number.isNaN(birthDate.getTime()) || birthDate > today) {
      setError(t("birthInvalid"));
      return;
    }
    let age = today.getFullYear() - birthDate.getFullYear();
    if (today.getMonth() < birthDate.getMonth() ||
        (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate())) age--;
    if (age < 13 || age > 99) {
      setError(t("ageLimit"));
      return;
    }

    setLoading(true);
    try {
      const response = await fetch("/api/profile", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          identityType,
          name: identityType === "realName" ? `${firstName.trim()} ${lastName.trim()}` : null,
          username: identityType === "pseudonym" ? username.trim() : null,
          birthDate: birth
        })
      });
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error(data.error || "profileSaveError");
      navigate("/account-type");
    } catch (saveError) {
      setError(saveError.message === "Username already in use" ? t("usernameTaken") : t("profileSaveError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="popup-overlay">
      <section className="popup-container" aria-labelledby="profile-setup-title">
        <button className="popup-close" onClick={onClose || (() => navigate(-1))} type="button" aria-label={t("close")}>✕</button>

        <h2 id="profile-setup-title" className="popup-title">{t("profileTitle")}</h2>
        <label className="profile-birth-label" htmlFor="profile-birth-date">{t("birthIntro")}</label>
        <input id="profile-birth-date" type="date" className="profile-input birth-input" value={birth} onChange={(e) => setBirth(e.target.value)} max={localDateValue(new Date())} />

        <h3 className="profile-identity-heading">{t("profileIdentityTitle")}</h3>
        <div className="profile-choice-tabs" role="group" aria-label={t("profileIdentityTitle")}>
          <button type="button" className={identityType === "realName" ? "selected" : ""} onClick={() => setIdentityType("realName")}>{t("useRealName")}</button>
          <button type="button" className={identityType === "pseudonym" ? "selected" : ""} onClick={() => setIdentityType("pseudonym")}>{t("usePseudonym")}</button>
        </div>

        {identityType === "realName" ? <>
          <div className="name-fields">
            <input className="profile-input" autoComplete="given-name" placeholder={t("firstName")} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            <input className="profile-input" autoComplete="family-name" placeholder={t("lastName")} value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </div>
          <p className="popup-subtitle pseudonym-hint">{t("realNameHint")}</p>
        </> : <>
          <input className="profile-input" autoComplete="nickname" placeholder={t("choosePseudonym")} value={username} onChange={(e) => setUsername(e.target.value)} maxLength={24} />
          <button className="pseudo-btn" type="button" onClick={generateUsername}>{t("generate")}</button>
          <p className="popup-subtitle pseudonym-hint">{t("pseudonymHint")}</p>
        </>}
        {error && <p className="profile-error" role="alert">{error}</p>}
        <button className="popup-btn" onClick={handleContinue} type="button" disabled={loading}>{loading ? t("saving") : t("continue")}</button>
      </section>
    </div>
  );
}
